import { api } from '@udm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny, request } from '../../api/client';
import { useMe } from '../auth/session';
import { i18n } from '../../i18n';
import { Card } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { useWidth } from '../../ui/useWidth';

/**
 * Energy consumption (kWh per day, from cumulative energy counters; legacy history + live). One series → no legend;
 * bars end at the baseline with rounded tops, hover shows the day's value; per-client totals below.
 * TODO(charts): shared chart component, weekly/monthly buckets for long ranges, export.
 */
const RANGES = [7, 30, 90, 365] as const;
const H = 200;
const M = { top: 10, right: 12, bottom: 24, left: 52 };

const niceMax = (v: number) => {
  if (v <= 5) return 5;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].map((m) => m * p).find((c) => c >= v)!;
};

export function EnergyChart() {
  const { t } = useTranslation();
  const [client, setClient] = useState('');
  const ops = canAny(useMe().data, 'operations.view');
  const [days, setDays] = useState<number>(30);
  const q = new URLSearchParams({ days: String(days) });
  if (client) q.set('client_id', client);
  const stats = useQuery({
    queryKey: ['stats', 'energy', client, days],
    queryFn: () =>
      request('GET', `/api/v1/stats/energy?${q.toString()}`, undefined, (v) => api.EnergyStats.parse(v)),
    refetchInterval: 10 * 60_000,
  });
  // The client list comes from the unfiltered call so the selector stays complete.
  const all = useQuery({
    queryKey: ['stats', 'energy', '', days],
    queryFn: () =>
      request('GET', `/api/v1/stats/energy?days=${days}`, undefined, (v) => api.EnergyStats.parse(v)),
    enabled: client !== '',
  });
  const clients = (client ? all.data?.clients : stats.data?.clients) ?? [];
  const total = (stats.data?.days ?? []).reduce((s, d) => s + d.kwh, 0);
  return (
    <Card
      title={t('dashboard.energy.title')}
      actions={
        <span className="toolbar chart-filters">
          {/* the authority observer (no operations.view): its own client only, no selector */}
          {ops && (
            <select
              className="input"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              aria-label={t('common.site')}
            >
              <option value="">{t('dashboard.allClients')}</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <select
            className="input"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label={t('dashboard.range')}
          >
            {RANGES.map((d) => (
              <option key={d} value={d}>
                {t('dashboard.lastDays', { count: d })}
              </option>
            ))}
          </select>
        </span>
      }
    >
      {stats.data && stats.data.days.length > 0 ? (
        <>
          <p className="muted small" style={{ marginTop: 0 }}>
            {t('dashboard.energy.total', { kwh: formatNumber(total, 0) })}
          </p>
          <Bars days={stats.data.days} />
          {!client && (
            <div className="client-rows" role="table" aria-label={t('dashboard.energy.perClient')}>
              <div className="client-row client-row-head energy-row" role="row">
                <span>{t('dashboard.client')}</span>
                <span className="num">kWh</span>
                <span className="num">{t('dashboard.devicesTotal')}</span>
                <span className="num">{t('dashboard.energy.perDevice')}</span>
              </div>
              {stats.data.clients.map((c) => (
                <button
                  key={c.client_id}
                  type="button"
                  role="row"
                  className="client-row energy-row"
                  onClick={() => setClient(c.client_id)}
                >
                  <span className="ellipsis">{c.name}</span>
                  <span className="num strong">{formatNumber(c.kwh, 0)}</span>
                  <span className="num muted">{formatNumber(c.devices)}</span>
                  <span className="num muted">{c.devices ? formatNumber(c.kwh / c.devices, 1) : '—'}</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          {stats.isLoading ? t('common.loading') : t('dashboard.energy.empty')}
        </p>
      )}
    </Card>
  );
}

function Bars({ days }: { days: api.EnergyStats['days'] }) {
  const { t } = useTranslation();
  const [box, W] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const plotW = Math.max(W - M.left - M.right, 10);
  const yMax = niceMax(Math.max(...days.map((d) => d.kwh), 1));
  const slot = plotW / days.length;
  const gap = slot > 6 ? 2 : 0;
  const bw = Math.max(slot - gap, 1);
  const y = (v: number) => M.top + (1 - v / yMax) * (H - M.top - M.bottom);
  const dayFmt = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' });
  const nTicks = Math.max(2, Math.min(8, Math.floor(plotW / 90)));
  const xTicks = [
    ...new Set(Array.from({ length: nTicks }, (_, i) => Math.round((i * (days.length - 1)) / (nTicks - 1)))),
  ];
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.floor((e.clientX - r.left - M.left) / slot);
    setHover(i >= 0 && i < days.length ? i : null);
  };
  const h = hover !== null ? days[hover]! : null;
  return (
    <div className="line-chart" ref={box}>
      {W > 0 && (
        <svg
          width={W}
          height={H}
          role="img"
          aria-label={t('dashboard.energy.title')}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {[0, 0.5, 1].map((f) => (
            <g key={f}>
              <line className="grid" x1={M.left} x2={W - M.right} y1={y(yMax * f)} y2={y(yMax * f)} />
              <text className="axis" x={M.left - 8} y={y(yMax * f) + 4} textAnchor="end">
                {formatNumber(yMax * f)}
              </text>
            </g>
          ))}
          {days.map((d, i) => {
            const top = y(d.kwh);
            const height = Math.max(H - M.bottom - top, 0);
            const r = Math.min(4, bw / 2, height);
            const x = M.left + i * slot + gap / 2;
            // rounded top, square base anchored on the baseline
            const path = `M${x},${H - M.bottom}V${top + r}Q${x},${top} ${x + r},${top}H${x + bw - r}Q${x + bw},${top} ${x + bw},${top + r}V${H - M.bottom}Z`;
            return <path key={d.day} d={path} className={`bar${hover === i ? ' is-hover' : ''}`} />;
          })}
          {xTicks.map((i, k) => (
            <text
              key={i}
              className="axis"
              x={M.left + i * slot + slot / 2}
              y={H - 6}
              textAnchor={k === 0 ? 'start' : k === xTicks.length - 1 ? 'end' : 'middle'}
            >
              {dayFmt.format(new Date(days[i]!.day))}
            </text>
          ))}
        </svg>
      )}
      {h && (
        <div
          className="chart-tooltip"
          style={{ left: Math.min(Math.max(M.left + hover! * slot + slot / 2, 80), W - 80) }}
        >
          <strong>{dayFmt.format(new Date(h.day))}</strong>
          <span>{formatNumber(h.kwh, 1)} kWh</span>
          <span className="muted">{t('dashboard.energy.devices', { count: h.devices })}</span>
        </div>
      )}
    </div>
  );
}
